import { useEffect, useState } from 'react';
import cls from 'classnames';
import { Button } from 'react-bootstrap';
import { useFormatLocalization } from 'src/utils/hooks';
import { useModalDialog, ConfirmModalDialog } from 'src/components/base/ModalDialog';
import classes from './ImageLightbox.module.scss';

export const ImageLightbox = ({
  show = false,
  images = [],
  index = 0,
  onClose,
  onDelete,
  onDownload,
  title,
}) => {
  const [currentIndex, setCurrentIndex] = useState(index);
  const deleteModal = useModalDialog();

  const selectedImage = images[currentIndex] ?? {};
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;
  const hasDelete = !!onDelete;
  const showDownload = !!onDownload;

  const defaultTitle = useFormatLocalization(__('Image: {index} of {total}'), {
    index: currentIndex + 1, total: images.length
  }, [currentIndex, images.length]);
  const lightboxTitle = title ?? defaultTitle;

  useEffect(() => {
    setCurrentIndex(index);
  }, [index, images.length]);

  useEffect(() => {
    if (!show) { return undefined; }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose?.();
      } else if (event.key === 'ArrowLeft' && hasPrevious) {
        setCurrentIndex((value) => value - 1);
      } else if (event.key === 'ArrowRight' && hasNext) {
        setCurrentIndex((value) => value + 1);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, hasPrevious, hasNext, onClose]);

  useEffect(() => {
    if (!show) { return undefined; }
    const previousOverflow = document.body.style.overflow;
    document.body.classList.add('image-lightbox-open');
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('image-lightbox-open');
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  if (!show) { return null; }

  async function handleDeleteClick() {
    if (!hasDelete) { return; }
    if (await deleteModal.show({ text: __('Do you want to delete this image?') })) {
      await onDelete(selectedImage, currentIndex);
      if (images.length <= 1) {
        onClose?.();
        return;
      }
      setCurrentIndex((value) => Math.max(0, value - 1));
    }
  }

  function handleDownloadClick() {
    if (!selectedImage?.file_url) { return; }
    if (typeof onDownload === 'function') {
      onDownload(selectedImage, currentIndex);
      return;
    }
    const a = document.createElement('a');
    a.href = selectedImage.file_url;
    a.download = selectedImage.name ?? `image-${currentIndex + 1}`;
    a.target = '_blank';
    a.rel = 'noopener';
    a.click();
  }

  return (
    <>
      <div className={classes.backdrop} onClick={() => onClose?.()} aria-hidden="true" />
      <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={lightboxTitle}>
        <div className={classes.header}>
          <span className={classes.title}>{lightboxTitle}</span>
          <button type="button" className={classes.closeButton} onClick={() => onClose?.()} aria-label={__('Close')}>×</button>
        </div>

        <div className={classes.main}>
          {hasPrevious && (
            <button type="button" className={cls(classes.arrow, classes.prev)} onClick={() => setCurrentIndex(currentIndex - 1)} aria-label={__('Previous')}>‹</button>
          )}
          <div className={classes.imageFrame}>
            <img src={selectedImage.file_url} alt={selectedImage.name ?? ''} className={classes.image} />
          </div>
          {hasNext && (
            <button type="button" className={cls(classes.arrow, classes.next)} onClick={() => setCurrentIndex(currentIndex + 1)} aria-label={__('Next')}>›</button>
          )}
        </div>

        <div className={classes.footer}>
          {hasDelete && <Button variant="danger" onClick={handleDeleteClick}>{__('Delete Image')}</Button>}
          <div className="flex-fill" />
          {showDownload && <Button variant="outline-light" onClick={handleDownloadClick}>{__('Download this photo')}</Button>}
          <Button variant="secondary" onClick={() => onClose?.()}>{__('Close')}</Button>
        </div>
      </div>

      {hasDelete && <ConfirmModalDialog {...deleteModal.register()} />}
    </>
  );
};
